import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// MVT Slice 3 (T-S3-WS4-A02) — dedicated trim page UI component.
//
// Lets a user pick a source video, scrub it, set in/out points on a timeline
// (touch + keyboard accessible via native range inputs), preview the selection,
// reset, cancel, and confirm. Confirm uploads the source and asks the server to
// clip it — but the apply/status endpoints + background job are A03, so when
// `applyUrlValue` is blank the confirm step stops at a "ready" placeholder
// instead of hitting a non-existent endpoint.
const MIN_SEGMENT_SECONDS = 0.5;
// Must match `.video-trim__range` thumb width in _video_trim.scss. Native range
// thumbs are inset by half their width at each end, so the selection fill and
// playhead are positioned in pixels with the same inset to line their edges up
// with the *center* of each handle.
const THUMB_WIDTH = 22;
// How long the centered play/pause control stays visible during playback before
// auto-hiding so it does not block the video.
const PLAYBACK_HIDE_MS = 2000;

export default class extends Controller {
  static targets = [
    "fileInput",
    "editor",
    "emptyState",
    "previewFrame",
    "preview",
    "sourceMeta",
    "fileInfo",
    "fileName",
    "timeline",
    "selection",
    "playheadIndicator",
    "startRange",
    "endRange",
    "startInput",
    "endInput",
    "durationLabel",
    "selectionLabel",
    "playheadLabel",
    "playbackButton",
    "playbackIcon",
    "resetButton",
    "confirmButton",
    "progress",
    "progressBar",
    "progressPct",
    "status",
  ];

  static values = {
    applyUrl: String,
    statusUrl: String,
    directUploadUrl: String,
    target: String,
    recordId: String,
    cancelUrl: String,
  };

  connect() {
    this.duration = 0;
    this.sourceFile = null;
    this.sourceWidth = 0;
    this.sourceHeight = 0;
    this.segmentPreviewActive = false;
    this.busy = false;
    this.confirmDefaultLabel = this.hasConfirmButtonTarget
      ? this.confirmButtonTarget.textContent.trim()
      : "Use this clip";
    this.renderEmptyState();
    this.updateControls();
  }

  openFilePicker() {
    if (this.busy) return;
    this.fileInputTarget.click();
  }

  fileChanged() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    this.sourceFile = file;
    this.segmentPreviewActive = false;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    this.previewTarget.src = this.objectUrl;
    this.emptyStateTarget.hidden = true;
    this.previewFrameTarget.hidden = false;
    this.fileInfoTarget.hidden = false;
    this.setStatus("Loading video…");
    this.updateControls();
  }

  onMetadata() {
    const preview = this.previewTarget;
    this.duration = preview.duration || 0;
    this.sourceWidth = preview.videoWidth || 0;
    this.sourceHeight = preview.videoHeight || 0;

    [this.startRangeTarget, this.endRangeTarget].forEach((range) => {
      range.min = "0";
      range.max = String(this.duration);
    });
    this.startInputTarget.max = String(this.duration);
    this.endInputTarget.max = String(this.duration);

    this.setRange(0, this.duration);
    this.previewTarget.currentTime = 0;
    this.renderPlayhead(0);

    if (this.sourceFile) {
      this.fileNameTarget.textContent = this.sourceFile.name;
      this.sourceMetaTarget.textContent = `${this.formatBytes(this.sourceFile.size)} · ${this.duration.toFixed(
        1
      )}s · ${this.sourceWidth}×${this.sourceHeight}`;
    }
    this.setStatus("Drag the handles to choose the part you want to keep.");
  }

  startRangeChanged() {
    let start = parseFloat(this.startRangeTarget.value) || 0;
    const end = parseFloat(this.endRangeTarget.value) || 0;
    if (start > end - MIN_SEGMENT_SECONDS) {
      start = Math.max(0, end - MIN_SEGMENT_SECONDS);
    }
    this.setRange(start, end);
    this.keepPlayheadInSelection();
  }

  endRangeChanged() {
    const start = parseFloat(this.startRangeTarget.value) || 0;
    let end = parseFloat(this.endRangeTarget.value) || 0;
    if (end < start + MIN_SEGMENT_SECONDS) {
      end = Math.min(this.duration, start + MIN_SEGMENT_SECONDS);
    }
    this.setRange(start, end);
    this.keepPlayheadInSelection();
  }

  startInputChanged() {
    const start = parseFloat(this.startInputTarget.value) || 0;
    const end = parseFloat(this.endRangeTarget.value) || 0;
    this.setRange(start, end);
    this.keepPlayheadInSelection();
  }

  endInputChanged() {
    const start = parseFloat(this.startRangeTarget.value) || 0;
    const end = parseFloat(this.endInputTarget.value) || 0;
    this.setRange(start, end);
    this.keepPlayheadInSelection();
  }

  togglePlayback() {
    if (!this.sourceFile || this.busy) return;
    if (!this.previewTarget.paused) {
      this.pausePreview();
      return;
    }
    this.playSelection();
  }

  playSegment() {
    this.playSelection();
  }

  playSelection() {
    const { start, end } = this.readRange();
    if (!(end > start)) return;
    this.segmentPreviewActive = true;
    const current = this.previewTarget.currentTime || 0;
    if (current < start || current >= end - 0.05) {
      this.previewTarget.currentTime = start;
      this.renderPlayhead(start);
    }
    this.previewTarget.play().catch(() => {
      this.segmentPreviewActive = false;
      this.onPlaybackStateChanged();
    });
  }

  onTimeUpdate() {
    const currentTime = this.previewTarget.currentTime || 0;
    this.playheadLabelTarget.textContent = this.formatTime(currentTime);
    this.renderPlayhead(currentTime);
    if (!this.segmentPreviewActive) return;
    const { end } = this.readRange();
    if (currentTime >= end - 0.05) {
      this.pausePreview();
      this.previewTarget.currentTime = end;
      this.renderPlayhead(end);
      this.segmentPreviewActive = false;
    }
  }

  onPlaybackStateChanged() {
    if (!this.hasPlaybackButtonTarget) return;
    const isPlaying = !this.previewTarget.paused;
    this.playbackButtonTarget.setAttribute("aria-label", isPlaying ? "Pause selected clip" : "Play selected clip");
    this.playbackButtonTarget.classList.toggle("video-trim__playback--playing", isPlaying);
    // Paused → keep the control visible so the user can resume; playing →
    // reveal then auto-hide so it stops obstructing the video.
    if (isPlaying) {
      this.revealPlayback();
    } else {
      this.showPlayback();
    }
  }

  // Reveal the control and, while playing, schedule it to fade out again.
  revealPlayback() {
    if (!this.hasPlaybackButtonTarget || !this.sourceFile) return;
    this.showPlayback();
    if (!this.previewTarget.paused) {
      this.hideTimer = setTimeout(() => {
        if (!this.previewTarget.paused) {
          this.playbackButtonTarget.classList.add("video-trim__playback--hidden");
        }
      }, PLAYBACK_HIDE_MS);
    }
  }

  showPlayback() {
    if (!this.hasPlaybackButtonTarget) return;
    clearTimeout(this.hideTimer);
    this.playbackButtonTarget.classList.remove("video-trim__playback--hidden");
  }

  reset() {
    if (!this.sourceFile) return;
    this.setRange(0, this.duration);
    this.previewTarget.currentTime = 0;
    this.renderPlayhead(0);
    this.setStatus("Selection reset to the full video.");
  }

  clearVideo() {
    if (this.busy) return;
    this.pausePreview();
    this.segmentPreviewActive = false;
    this.duration = 0;
    this.sourceFile = null;
    this.sourceWidth = 0;
    this.sourceHeight = 0;
    this.fileInputTarget.value = "";
    this.previewTarget.removeAttribute("src");
    this.previewTarget.load();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.renderEmptyState();
    this.setRange(0, 0);
    this.renderPlayhead(0);
    this.setStatus("");
  }

  cancel() {
    if (this.hasCancelUrlValue && this.cancelUrlValue) {
      window.location.assign(this.cancelUrlValue);
    } else {
      window.history.back();
    }
  }

  async confirm() {
    if (this.busy) return;
    const { start, end } = this.readRange();
    if (!this.sourceFile || !(end - start >= MIN_SEGMENT_SECONDS)) {
      this.setStatus("Choose a clip of at least half a second first.");
      return;
    }

    if (!this.applyUrlValue) {
      this.setStatus(
        `Ready: ${this.formatTime(start)}–${this.formatTime(end)} (${this.formatTime(end - start)}).`
      );
      return;
    }

    this.setBusy(true);
    this.setConfirmLabel("Uploading…");
    this.showProgress();
    this.setProgress(0);
    try {
      this.setStatus("Uploading your video…");
      const signedId = await this.directUpload(this.sourceFile, (progress) => {
        this.setProgress(progress);
        this.setStatus(`Uploading your video… ${Math.round(progress * 100)}%`);
      });

      // Upload done; the server-side clip submission has no byte progress, so
      // switch the bar to an indeterminate "working" state.
      this.setProgress(1);
      this.setConfirmLabel("Processing…");
      this.showProgress({ indeterminate: true });
      this.setStatus("Trimming on the server…");
      await this.submitTrim(signedId, start, end);

      // Finalization runs in the background (Videos::ProcessTrimJob); send the
      // user back to the page that shows the processing placeholder.
      this.setStatus("Your trimmed video is processing…");
      if (this.hasCancelUrlValue && this.cancelUrlValue) {
        window.location.assign(this.cancelUrlValue);
      } else {
        this.setStatus("Your trimmed video is processing. You can head back now.");
        this.hideProgress();
        this.setConfirmLabel(this.confirmDefaultLabel);
        this.setBusy(false);
      }
    } catch (error) {
      this.setStatus(`Something went wrong: ${error.message}`);
      this.hideProgress();
      this.setConfirmLabel(this.confirmDefaultLabel);
      this.setBusy(false);
    }
  }

  // --- helpers ---

  readRange() {
    return {
      start: parseFloat(this.startRangeTarget.value) || 0,
      end: parseFloat(this.endRangeTarget.value) || 0,
    };
  }

  setRange(start, end) {
    let s = Math.max(0, Math.min(start, this.duration));
    let e = Math.max(0, Math.min(end, this.duration));
    if (e < s + MIN_SEGMENT_SECONDS) {
      e = Math.min(this.duration, s + MIN_SEGMENT_SECONDS);
      if (e <= s) s = Math.max(0, e - MIN_SEGMENT_SECONDS);
    }

    this.startRangeTarget.value = String(s);
    this.endRangeTarget.value = String(e);
    this.startInputTarget.value = s.toFixed(1);
    this.endInputTarget.value = e.toFixed(1);

    this.renderSelection(s, e);
    this.updateControls();
  }

  renderSelection(start, end) {
    const leftPx = this.pxForTime(start);
    const rightPx = this.pxForTime(end);
    this.selectionTarget.style.left = `${leftPx}px`;
    this.selectionTarget.style.width = `${Math.max(0, rightPx - leftPx)}px`;
    this.durationLabelTarget.textContent = `Duration ${this.formatTime(this.duration)}`;
    this.selectionLabelTarget.textContent = `${this.formatTime(end - start)} selected`;
  }

  renderPlayhead(seconds) {
    if (!this.hasPlayheadIndicatorTarget) return;
    this.playheadIndicatorTarget.style.left = `${this.pxForTime(seconds)}px`;
  }

  // --- timeline geometry (thumb-inset aware so visuals align to handle centers) ---

  usableTrackWidth() {
    const width = this.timelineTarget.getBoundingClientRect().width;
    return Math.max(1, width - THUMB_WIDTH);
  }

  pxForTime(seconds) {
    const ratio = this.duration ? Math.max(0, Math.min(1, seconds / this.duration)) : 0;
    return THUMB_WIDTH / 2 + ratio * this.usableTrackWidth();
  }

  timeForClientX(clientX) {
    const rect = this.timelineTarget.getBoundingClientRect();
    const x = clientX - rect.left - THUMB_WIDTH / 2;
    const ratio = Math.max(0, Math.min(1, x / this.usableTrackWidth()));
    return ratio * this.duration;
  }

  // Drag the playhead (or click anywhere on the open track) to seek the preview.
  beginScrub(event) {
    if (!this.sourceFile || this.busy) return;
    // A pointerdown on a trim handle bubbles up from the range input to the
    // timeline; ignore it so grabbing a handle never also scrubs the playhead.
    if (event.target === this.startRangeTarget || event.target === this.endRangeTarget) return;
    event.preventDefault();
    this.segmentPreviewActive = false;
    this.scrubbing = true;
    this.scrubTo(event);
    this.boundScrubMove = this.boundScrubMove || ((e) => this.scrubTo(e));
    this.boundScrubEnd = this.boundScrubEnd || (() => this.endScrub());
    document.addEventListener("pointermove", this.boundScrubMove);
    document.addEventListener("pointerup", this.boundScrubEnd);
  }

  scrubTo(event) {
    if (!this.scrubbing) return;
    const time = this.timeForClientX(event.clientX);
    this.previewTarget.currentTime = time;
    this.renderPlayhead(time);
    this.playheadLabelTarget.textContent = this.formatTime(time);
  }

  endScrub() {
    this.scrubbing = false;
    document.removeEventListener("pointermove", this.boundScrubMove);
    document.removeEventListener("pointerup", this.boundScrubEnd);
  }

  keepPlayheadInSelection() {
    if (!this.sourceFile) return;
    const { start, end } = this.readRange();
    const current = this.previewTarget.currentTime || 0;
    if (current < start || current > end) {
      this.previewTarget.currentTime = start;
      this.renderPlayhead(start);
    }
  }

  pausePreview() {
    if (!this.previewTarget.paused) this.previewTarget.pause();
    this.onPlaybackStateChanged();
  }

  renderEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false;
    if (this.hasPreviewFrameTarget) this.previewFrameTarget.hidden = true;
    if (this.hasFileInfoTarget) this.fileInfoTarget.hidden = true;
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = "No video selected";
    if (this.hasSourceMetaTarget) this.sourceMetaTarget.textContent = "";
    this.durationLabelTarget.textContent = "Duration 0:00";
    this.playheadLabelTarget.textContent = "0:00";
    this.selectionLabelTarget.textContent = "0:00 selected";
  }

  updateControls() {
    const { start, end } = this.readRange();
    const valid = !!this.sourceFile && end - start >= MIN_SEGMENT_SECONDS;
    const enabled = valid && !this.busy;
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = !enabled;
    if (this.hasPlaybackButtonTarget) this.playbackButtonTarget.disabled = !enabled;
    if (this.hasResetButtonTarget) this.resetButtonTarget.disabled = !this.sourceFile || this.busy;
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.updateControls();
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
  }

  setConfirmLabel(text) {
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.textContent = text;
  }

  showProgress({ indeterminate = false } = {}) {
    if (!this.hasProgressTarget) return;
    this.progressTarget.hidden = false;
    this.progressTarget.classList.toggle("video-trim__progress--indeterminate", indeterminate);
  }

  setProgress(fraction) {
    const pct = Math.max(0, Math.min(100, Math.round((fraction || 0) * 100)));
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("video-trim__progress--indeterminate");
      this.progressTarget.setAttribute("aria-valuenow", String(pct));
    }
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`;
    if (this.hasProgressPctTarget) this.progressPctTarget.textContent = `${pct}%`;
  }

  hideProgress() {
    if (!this.hasProgressTarget) return;
    this.progressTarget.hidden = true;
    this.progressTarget.classList.remove("video-trim__progress--indeterminate");
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%";
  }

  directUpload(file, onProgress) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable && onProgress) {
              onProgress(event.loaded / event.total);
            }
          });
        },
      });
      upload.create((error, blob) => {
        if (error) reject(error);
        else resolve(blob.signed_id);
      });
    });
  }

  submitTrim(signedId, start, end) {
    const body = new FormData();
    body.append("signed_id", signedId);
    body.append("start_sec", String(start));
    body.append("end_sec", String(end));
    body.append("source_width", String(this.sourceWidth || 0));
    body.append("source_height", String(this.sourceHeight || 0));

    return fetch(this.applyUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body,
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.ok) return payload;
      const detail = payload.error || response.statusText || `HTTP ${response.status}`;
      throw new Error(detail);
    });
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.content : "";
  }

  formatTime(seconds) {
    const s = Math.max(0, seconds || 0);
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }

  formatBytes(bytes) {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i += 1;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  }

  disconnect() {
    clearTimeout(this.hideTimer);
    if (this.boundScrubMove) document.removeEventListener("pointermove", this.boundScrubMove);
    if (this.boundScrubEnd) document.removeEventListener("pointerup", this.boundScrubEnd);
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }
}
